import * as React from 'react';
import { MenuItem, Select, Stack, SxProps, Theme, Typography } from '@mui/material';
import { IReportPeriodResponse } from '../../../shared/services/report-period/report-period.model';

/**
 * The "Compare to" selector for the analytics and hotspots surfaces. Value '' means either
 * "no comparison" (noneOption, the analytics dashboard: comparing is opt-in) or "automatic"
 * (hotspots keeps deriving the previous period, since comparison is that page's purpose).
 * Any other value is the id of the report period to compare against. Renders nothing when
 * the organisation has fewer than two report periods, because there is nothing to compare.
 */
export const ComparisonPeriodSelect: React.FC<{
  /** '' = no comparison (noneOption) or automatic previous period, otherwise a report period id. */
  value: string;
  onChange: (next: string) => void;
  reportPeriods: IReportPeriodResponse[];
  /** The period being reported on; excluded from the options. */
  selectedReportPeriodId: string;
  /** '' means "No comparison" instead of the automatic previous period. */
  noneOption?: boolean;
  /** What automatic currently resolves to, so the closed control names the period. */
  autoComparisonPeriod?: IReportPeriodResponse;
  /** Stretch to fill the row (mobile layouts). */
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}> = ({ value, onChange, reportPeriods, selectedReportPeriodId, noneOption = false, autoComparisonPeriod, fullWidth = false, sx }) => {
  if (reportPeriods.length < 2) return null;

  const options = [...reportPeriods]
    .filter((period) => period.id !== selectedReportPeriodId)
    .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());

  // An id with no matching option (deleted period, or the global selector moved onto the chosen
  // period) displays as automatic, so the Select never holds an out-of-range value. The raw value
  // is kept upstream: it becomes meaningful again if the global selector moves off that period.
  const effectiveValue = options.some((period) => period.id === value) ? value : '';

  return (
    <Stack direction='row' alignItems='center' spacing={1} sx={{ width: fullWidth ? '100%' : 'auto', ...sx }}>
      <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>Compare to:</Typography>
      <Select
        size='small'
        displayEmpty
        value={effectiveValue}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ 'aria-label': 'Comparison report period' }}
        renderValue={(selected) => {
          if (selected) return options.find((period) => period.id === selected)?.friendlyName ?? '';
          if (noneOption) return 'No comparison';
          return autoComparisonPeriod
            ? `${autoComparisonPeriod.friendlyName} (automatic)`
            : 'Previous period (automatic)';
        }}
        // 168 still fits the widest option ("2025 (automatic)"); 190 left the analytics
        // toolbar row one pixel too wide beside the dimension selector at 1500px.
        sx={{ minWidth: 168, height: 40, flexGrow: fullWidth ? 1 : 0 }}
      >
        <MenuItem value=''>{noneOption ? 'No comparison' : 'Previous period (automatic)'}</MenuItem>
        {options.map((period) => (
          <MenuItem key={period.id} value={period.id}>{period.friendlyName}</MenuItem>
        ))}
      </Select>
    </Stack>
  );
};
